import axios from "axios";
import API_URL from "../Config";

const getAuthKey = () => localStorage.getItem("authKey");

const withAuth = (config = {}) => ({
  ...config,
  headers: {
    Authorization: getAuthKey(),
    ...(config.headers || {}),
  },
});

const unwrap = async (request) => {
  const response = await request;
  return response.data;
};

const appendValue = (formData, key, value) => {
  if (value === undefined || value === null || value === "") {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => appendValue(formData, key, item));
    return;
  }

  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value));
    return;
  }

  formData.append(key, value);
};

const buildFileFormData = (fieldName, files = []) => {
  const formData = new FormData();
  files.forEach((file) => formData.append(fieldName, file));
  return formData;
};

const buildSessionAttachmentsFormData = ({
  attendanceSheets = [],
  photographs = [],
  materials = [],
  attachments = [],
  photographCaptions = [],
} = {}) => {
  const formData = new FormData();

  attendanceSheets.forEach((file) =>
    formData.append("attendance_sheet", file),
  );
  photographs.forEach((file) => formData.append("photographs", file));
  materials.forEach((file) => formData.append("materials", file));
  attachments.forEach((file) => formData.append("attachments", file));
  photographCaptions.forEach((caption) =>
    formData.append("photograph_captions", caption || ""),
  );

  return formData;
};

const buildTrainingSessionCreateFormData = ({
  session = {},
  participants = [],
  sessionFiles = {},
  participantFilesByEmployee = {},
} = {}) => {
  const formData = buildSessionAttachmentsFormData(sessionFiles);

  Object.entries(session).forEach(([key, value]) => {
    appendValue(formData, key, value);
  });

  formData.append("participants", JSON.stringify(participants));

  Object.entries(participantFilesByEmployee).forEach(([employeeId, files]) => {
    (files || []).forEach((file) => {
      formData.append(`certificate_${employeeId}`, file);
    });
  });

  return formData;
};

const isFormDataPayload = (payload) =>
  typeof FormData !== "undefined" && payload instanceof FormData;

const postJson = (path, payload) =>
  unwrap(
    axios.post(
      `${API_URL}${path}`,
      payload,
      withAuth({ headers: { "Content-Type": "application/json" } }),
    ),
  );

const putJson = (path, payload) =>
  unwrap(
    axios.put(
      `${API_URL}${path}`,
      payload,
      withAuth({ headers: { "Content-Type": "application/json" } }),
    ),
  );

const deleteJson = (path, config = {}) =>
  unwrap(axios.delete(`${API_URL}${path}`, withAuth(config)));

const postFormData = (path, formData) =>
  unwrap(
    axios.post(
      `${API_URL}${path}`,
      formData,
      withAuth({ headers: { "Content-Type": "multipart/form-data" } }),
    ),
  );

export const hrRecordsApi = {
  fetchEmployeesDropdown() {
    return unwrap(
      axios.get(`${API_URL}/api/dropdown/available_manpower`, withAuth()),
    );
  },

  searchEmployees(search = "") {
    return unwrap(
      axios.get(
        `${API_URL}/api/hr/employees/search`,
        withAuth({ params: { search } }),
      ),
    );
  },

  getTrainingCategories() {
    return unwrap(
      axios.get(`${API_URL}/api/hr/training-categories`, withAuth()),
    );
  },

  getMedicalTypes() {
    return unwrap(axios.get(`${API_URL}/api/hr/medical-types`, withAuth()));
  },

  listTrainingStandards(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/training-standards`, withAuth({ params })),
    );
  },

  createTrainingStandard(payload) {
    return postJson(`/api/hr/training-standards`, payload);
  },

  updateTrainingStandard(standardId, payload) {
    return putJson(`/api/hr/training-standards/${standardId}`, payload);
  },

  suggestTrainingReferences(params = {}) {
    return unwrap(
      axios.get(
        `${API_URL}/api/hr/training-references/suggest`,
        withAuth({ params }),
      ),
    );
  },

  listTrainingMasters(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/training-masters`, withAuth({ params })),
    );
  },

  getTrainingMaster(masterId) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/training-masters/${masterId}`, withAuth()),
    );
  },

  createTrainingMaster(payload) {
    return postJson(`/api/hr/training-masters`, payload);
  },

  updateTrainingMaster(masterId, payload) {
    return putJson(`/api/hr/training-masters/${masterId}`, payload);
  },

  deleteTrainingMaster(masterId) {
    return unwrap(
      axios.delete(`${API_URL}/api/hr/training-masters/${masterId}`, withAuth()),
    );
  },

  listTrainingSessions(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/training-sessions`, withAuth({ params })),
    );
  },

  getTrainingSession(sessionId) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/training-sessions/${sessionId}`, withAuth()),
    );
  },

  createTrainingSession(payload) {
    if (isFormDataPayload(payload)) {
      return postFormData(`/api/hr/training-sessions`, payload);
    }

    return postJson(`/api/hr/training-sessions`, payload);
  },

  updateTrainingSession(sessionId, payload) {
    return putJson(`/api/hr/training-sessions/${sessionId}`, payload);
  },

  deleteTrainingSession(sessionId) {
    return unwrap(
      axios.delete(`${API_URL}/api/hr/training-sessions/${sessionId}`, withAuth()),
    );
  },

  saveTrainingParticipants(sessionId, payload) {
    return postJson(`/api/hr/training-sessions/${sessionId}/participants`, payload);
  },

  updateTrainingParticipant(participantId, payload) {
    return putJson(`/api/hr/training-participants/${participantId}`, payload);
  },

  removeTrainingParticipant(participantId) {
    return unwrap(
      axios.delete(
        `${API_URL}/api/hr/training-participants/${participantId}`,
        withAuth(),
      ),
    );
  },

  uploadTrainingSessionAttachments(sessionId, payload = {}) {
    return postFormData(
      `/api/hr/training-sessions/${sessionId}/attachments`,
      buildSessionAttachmentsFormData(payload),
    );
  },

  uploadParticipantCertificate(participantId, files = []) {
    return postFormData(
      `/api/hr/training-participants/${participantId}/certificate`,
      buildFileFormData("certificate", files),
    );
  },

  listParticipantAttachments(participantId) {
    return unwrap(
      axios.get(
        `${API_URL}/api/hr/training-participants/${participantId}/attachments`,
        withAuth(),
      ),
    );
  },

  deleteTrainingSessionAttachment(fileId) {
    return unwrap(
      axios.delete(
        `${API_URL}/api/hr/training-sessions/attachments/${fileId}`,
        withAuth(),
      ),
    );
  },

  deleteParticipantAttachment(fileId) {
    return unwrap(
      axios.delete(
        `${API_URL}/api/hr/training-participants/attachments/${fileId}`,
        withAuth(),
      ),
    );
  },

  downloadTrainingSessionAttachment(fileId) {
    return axios.get(
      `${API_URL}/api/hr/training-sessions/attachments/${fileId}/download`,
      withAuth({ responseType: "blob" }),
    );
  },

  viewTrainingSessionAttachment(fileId) {
    return axios.get(
      `${API_URL}/api/hr/training-sessions/attachments/${fileId}/view`,
      withAuth({ responseType: "blob" }),
    );
  },

  downloadParticipantAttachment(fileId) {
    return axios.get(
      `${API_URL}/api/hr/training-participants/attachments/${fileId}/download`,
      withAuth({ responseType: "blob" }),
    );
  },

  viewParticipantAttachment(fileId) {
    return axios.get(
      `${API_URL}/api/hr/training-participants/attachments/${fileId}/view`,
      withAuth({ responseType: "blob" }),
    );
  },

  getTrainingComplianceDashboard() {
    return unwrap(
      axios.get(
        `${API_URL}/api/hr/training-compliance/dashboard`,
        withAuth(),
      ),
    );
  },

  listTrainingDue(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/training-compliance/due`, withAuth({ params })),
    );
  },

  getTrainingComplianceMatrix(params = {}) {
    return unwrap(
      axios.get(
        `${API_URL}/api/hr/training-compliance/matrix`,
        withAuth({ params }),
      ),
    );
  },

  getEmployeeTrainingHistory(employeeId) {
    return unwrap(
      axios.get(
        `${API_URL}/api/hr/employees/${employeeId}/training-history`,
        withAuth(),
      ),
    );
  },

  listNotificationConfigs(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/notification-config`, withAuth({ params })),
    );
  },

  getNotificationConfig(moduleKey, params = {}) {
    return unwrap(
      axios.get(
        `${API_URL}/api/notification-config/${moduleKey}`,
        withAuth({ params }),
      ),
    );
  },

  updateNotificationConfig(moduleKey, payload, params = {}) {
    return unwrap(
      axios.put(
        `${API_URL}/api/notification-config/${moduleKey}`,
        payload,
        withAuth({
          params,
          headers: { "Content-Type": "application/json" },
        }),
      ),
    );
  },

  deleteNotificationConfig(moduleKey, params = {}) {
    return deleteJson(`/api/notification-config/${moduleKey}`, { params });
  },

  listEmailLog(params = {}) {
    return unwrap(axios.get(`${API_URL}/api/email-log`, withAuth({ params })));
  },

  getEmailLogEntry(logId) {
    return unwrap(axios.get(`${API_URL}/api/email-log/${logId}`, withAuth()));
  },

  getTrainingReminderConfig() {
    return this.getNotificationConfig("training");
  },

  updateTrainingReminderConfig(payload) {
    return this.updateNotificationConfig("training", payload);
  },

  getMedicalReminderConfig() {
    return this.getNotificationConfig("medical");
  },

  updateMedicalReminderConfig(payload) {
    return this.updateNotificationConfig("medical", payload);
  },

  getEmailConfigs(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/get_email_configs`, withAuth({ params })),
    );
  },

  getEmailTemplates() {
    return unwrap(
      axios.get(`${API_URL}/api/get_email_templates`, withAuth()),
    );
  },

  listMedicalReports(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/medical-reports`, withAuth({ params })),
    );
  },

  getMedicalReport(medicalId) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/medical-reports/${medicalId}`, withAuth()),
    );
  },

  createMedicalReport(payload) {
    return postJson(`/api/hr/medical-reports`, payload);
  },

  updateMedicalReport(medicalId, payload) {
    return putJson(`/api/hr/medical-reports/${medicalId}`, payload);
  },

  deleteMedicalReport(medicalId) {
    return unwrap(
      axios.delete(`${API_URL}/api/hr/medical-reports/${medicalId}`, withAuth()),
    );
  },

  uploadMedicalReportFile(medicalId, files = []) {
    return postFormData(
      `/api/hr/medical-reports/${medicalId}/report`,
      buildFileFormData("report", files),
    );
  },

  listMedicalAttachments(medicalId) {
    return unwrap(
      axios.get(
        `${API_URL}/api/hr/medical-reports/${medicalId}/attachments`,
        withAuth(),
      ),
    );
  },

  deleteMedicalAttachment(fileId) {
    return unwrap(
      axios.delete(
        `${API_URL}/api/hr/medical-reports/attachments/${fileId}`,
        withAuth(),
      ),
    );
  },

  downloadMedicalAttachment(fileId) {
    return axios.get(
      `${API_URL}/api/hr/medical-reports/attachments/${fileId}/download`,
      withAuth({ responseType: "blob" }),
    );
  },

  viewMedicalAttachment(fileId) {
    return axios.get(
      `${API_URL}/api/hr/medical-reports/attachments/${fileId}/view`,
      withAuth({ responseType: "blob" }),
    );
  },

  downloadAllMedicalAttachments(medicalId) {
    return axios.get(
      `${API_URL}/api/hr/medical-reports/${medicalId}/download-all`,
      withAuth({ responseType: "blob" }),
    );
  },

  downloadAllEmployeeMedicalReports(employeeId) {
    return axios.get(
      `${API_URL}/api/hr/employees/${employeeId}/medical-reports/download-all`,
      withAuth({ responseType: "blob" }),
    );
  },

  getMedicalComplianceDashboard() {
    return unwrap(
      axios.get(`${API_URL}/api/hr/medical-compliance/dashboard`, withAuth()),
    );
  },

  listMedicalDue(params = {}) {
    return unwrap(
      axios.get(`${API_URL}/api/hr/medical-compliance/due`, withAuth({ params })),
    );
  },

  listTrainingCertificates(params = {}) {
    return this.listTrainingSessions(params);
  },

  getTrainingCertificate(trainingId) {
    return this.getTrainingSession(trainingId);
  },

  createTrainingCertificate(payload) {
    return this.createTrainingSession(payload);
  },

  updateTrainingCertificate(trainingId, payload) {
    return this.updateTrainingSession(trainingId, payload);
  },

  deleteTrainingCertificate(trainingId) {
    return this.deleteTrainingSession(trainingId);
  },

  uploadTrainingAttachments(trainingId, files) {
    return this.uploadTrainingSessionAttachments(trainingId, { attachments: files });
  },

  deleteTrainingAttachment(fileId) {
    return this.deleteTrainingSessionAttachment(fileId);
  },

  downloadTrainingAttachment(fileId) {
    return this.downloadTrainingSessionAttachment(fileId);
  },

  viewTrainingAttachment(fileId) {
    return this.viewTrainingSessionAttachment(fileId);
  },

  uploadMedicalAttachments(medicalId, files) {
    return this.uploadMedicalReportFile(medicalId, files);
  },
};

export const hrRecordsFormDataHelpers = {
  appendValue,
  buildFileFormData,
  buildSessionAttachmentsFormData,
  buildTrainingSessionCreateFormData,
};
